import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TestimonialsSlider from '../components/TestimonialsSlider';
import FAQSection from '../components/FAQSection';
import ContactModal from '../components/ContactModal';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Shield, Award, Users, Clock, TrendingUp, CheckCircle, Heart, Zap } from 'lucide-react';

const AboutUsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-navy-900">
      <Navbar onOpenModal={() => setIsModalOpen(true)} />
      
      {/* Hero Section - About the Company */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-100 mb-6">
                About{' '}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Trademark Nexor
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                The IP firm built for founders, operators, and brand builders who move fast
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mt-16">
              <div className="animate-fade-in">
                <img
                  src="https://images.unsplash.com/photo-1760346546771-a81d986459ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW0lMjBzdHJhdGVneSUyMG1lZXRpbmclMjBjb3Jwb3JhdGV8ZW58MHx8fHwxNzczOTQwMDc2fDA&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Our Team"
                  className="rounded-2xl shadow-2xl hover-zoom"
                />
              </div>
              <div className="space-y-6 animate-slide-up">
                <h2 className="text-4xl font-bold text-gray-100">Who We Are</h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Trademark Nexor was founded on a simple idea: every business deserves access to serious intellectual property protection, regardless of size or budget. We've assembled a team of IP attorneys, filing specialists, and brand advisors who have collectively handled over 10,000 trademark applications across every major industry.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Our approach combines legal rigor with a technology-driven workflow that eliminates delays and keeps you informed at every stage. Whether you're filing your first trademark or managing a portfolio of brands, we deliver the same standard of precision and responsiveness.
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="flex items-center space-x-2">
                    <Award className="text-brand-primary" size={24} />
                    <span className="font-semibold text-gray-200">10,000+ Applications Filed</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="text-brand-primary" size={24} />
                    <span className="font-semibold text-gray-200">98% Approval Rate</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="text-brand-primary" size={24} />
                    <span className="font-semibold text-gray-200">24-Hour Filing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision and Mission */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
                Our Vision &{' '}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Mission
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                The principles behind every filing, every search, and every client interaction
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Vision */}
              <div className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all animate-scale-in">
                <div className="bg-brand-gradient w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Target className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-100 mb-4">Our Vision</h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  To become the default IP partner for every American business, making trademark registration as routine and accessible as opening a bank account. We're building toward a future where brand protection is never an afterthought.
                </p>
              </div>

              {/* Mission */}
              <div className="bg-gradient-to-br from-navy-800 to-navy-700 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all animate-scale-in" style={{animationDelay: '0.1s'}}>
                <div className="bg-brand-gradient w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <Heart className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-100 mb-4">Our Mission</h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  To deliver attorney-grade intellectual property services at flat-fee prices, backed by a process that's fast, transparent, and built around the needs of founders and growing businesses who can't afford to wait.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <div className="mt-16 grid md:grid-cols-3 gap-6">
              {[
                { icon: Shield, title: 'Transparency', desc: 'Flat fees, clear timelines, and honest assessments from the start' },
                { icon: Zap, title: 'Precision', desc: 'Every filing is reviewed for accuracy before it reaches the USPTO' },
                { icon: Users, title: 'Client-First', desc: 'Your assigned advisor knows your case and is always reachable' }
              ].map((value, idx) => {
                const Icon = value.icon;
                return (
                  <div key={idx} className="text-center p-6 bg-navy-700 rounded-xl shadow-md hover:shadow-lg transition-all animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                    <Icon className="text-brand-primary mx-auto mb-4" size={40} />
                    <h4 className="text-xl font-bold text-gray-100 mb-2">{value.title}</h4>
                    <p className="text-gray-300">{value.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
                Why Choose{' '}
                <span className="bg-brand-gradient bg-clip-text text-transparent">
                  Trademark Nexor
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Six reasons brands choose us over traditional law firms
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Target,
                  title: 'IP Specialists Only',
                  desc: 'We focus exclusively on trademarks, copyrights, and business filings. No generalists, no distractions.'
                },
                {
                  icon: Clock,
                  title: '24-Hour Filing',
                  desc: 'Submit your details and we prepare, review, and file your application by the next business day.'
                },
                {
                  icon: Award,
                  title: '98% Approval Rate',
                  desc: 'Our pre-filing clearance process catches conflicts before they become costly rejections.'
                },
                {
                  icon: Users,
                  title: 'Named Advisor',
                  desc: 'A dedicated professional assigned to your case who knows your brand and answers your calls.'
                },
                {
                  icon: Shield,
                  title: 'Full-Lifecycle Service',
                  desc: 'From initial search through registration, monitoring, and enforcement — we handle every stage.'
                },
                {
                  icon: TrendingUp,
                  title: 'No Hidden Fees',
                  desc: 'Our flat-fee packages include everything upfront. Government filing fees are clearly disclosed.'
                }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="bg-navy-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                    <div className="bg-brand-gradient w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-100 mb-3">{item.title}</h3>
                    <p className="text-base text-gray-300 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Button 
                size="lg"
                className="bg-brand-gradient text-white hover:opacity-90 group shadow-lg px-8 py-6 text-lg font-bold"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started Today
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={24} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* FAQs */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Your Brand Won't Protect Itself
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Join 10,000+ businesses that secured their brand identity with us. Start your filing today and own what you've built.
            </p>
            <Button 
              size="lg"
              onClick={() => setIsModalOpen(true)}
              className="bg-navy-500 text-white hover:bg-navy-400 shadow-xl px-10 py-6 text-lg font-bold border-2 border-navy-400"
            >
              Contact Us Now
              <ArrowRight className="ml-2" size={24} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AboutUsPage;
